import { useEffect, useState } from "react";
import { Loader2, Minus, Plus, X } from "lucide-react";
import {
  cropYieldAverages,
  defaultSeason,
  PRESET_CROPS,
  type FertPreset,
} from "../../lib/fertPreset";
import {
  CROP_REMOVAL,
  DEFAULT_YIELD,
  type Season,
} from "../../lib/replacement";
import type { DashboardField } from "../../types/contract";

type YieldOpt = { key: string; crop: string; year: number };

function resizeSeasons(current: Season[], count: number): Season[] {
  const next = Math.max(1, Math.min(8, count));
  if (next <= current.length) return current.slice(0, next);
  return [
    ...current,
    ...Array.from({ length: next - current.length }, () =>
      defaultSeason("soybean"),
    ),
  ];
}

function patchSeason(
  current: Season[],
  index: number,
  patch: Partial<Season>,
): Season[] {
  return current.map((season, i) =>
    i === index
      ? {
          ...season,
          ...patch,
          ...(patch.crop
            ? { yieldLevel: DEFAULT_YIELD[patch.crop] ?? season.yieldLevel }
            : {}),
        }
      : season,
  );
}

// Averages load once, when the user opts in. A failed fetch is {} rather than
// null, so Build unblocks and falls back to the static goals.
function useCropAverages(
  fieldId: string,
  yieldOpts: YieldOpt[],
  useHistory: boolean,
) {
  const [avgs, setAvgs] = useState<Record<string, number> | null>(null);
  const [loadingAvgs, setLoadingAvgs] = useState(false);
  const hasHistory = yieldOpts.length > 0;
  useEffect(() => {
    if (!useHistory || avgs !== null || loadingAvgs || !hasHistory) return;
    setLoadingAvgs(true);
    void cropYieldAverages(fieldId, yieldOpts)
      .then((loaded) => {
        setAvgs(loaded);
        setLoadingAvgs(false);
      })
      .catch(() => {
        setAvgs({});
        setLoadingAvgs(false);
      });
  }, [useHistory, avgs, loadingAvgs, hasHistory, fieldId, yieldOpts]);
  const goalFor = (season: Season) =>
    useHistory && avgs?.[season.crop] != null
      ? avgs[season.crop]
      : season.yieldLevel;
  return { avgs, loadingAvgs, hasHistory, goalFor };
}

function SeasonHeading({
  label,
  busy,
  onCancel,
}: {
  label: string;
  busy: boolean;
  onCancel: () => void;
}) {
  return (
    <div className="flex items-center justify-between border-b border-line px-3 py-2">
      <h2 className="text-sm font-semibold">
        {label} — seasons &amp; yield goals
      </h2>
      <button
        onClick={onCancel}
        disabled={busy}
        className="rounded p-1 hover:bg-hover disabled:opacity-50"
        aria-label="Close"
      >
        <X size={16} />
      </button>
    </div>
  );
}

function SeasonCount({
  count,
  onCount,
}: {
  count: number;
  onCount: (count: number) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <span>Seasons:</span>
      <button
        className="rounded border border-line p-1 hover:bg-hover"
        aria-label="Fewer seasons"
        onClick={() => onCount(count - 1)}
      >
        <Minus size={12} />
      </button>
      <span className="tnum w-5 text-center font-medium">{count}</span>
      <button
        className="rounded border border-line p-1 hover:bg-hover"
        aria-label="More seasons"
        onClick={() => onCount(count + 1)}
      >
        <Plus size={12} />
      </button>
    </div>
  );
}

function SeasonRows({
  seasons,
  goalFor,
  useHistory,
  avgs,
  onPatch,
}: {
  seasons: Season[];
  goalFor: (season: Season) => number | undefined;
  useHistory: boolean;
  avgs: Record<string, number> | null;
  onPatch: (index: number, patch: Partial<Season>) => void;
}) {
  return (
    <ul className="flex flex-col gap-1.5">
      {seasons.map((season, index) => (
        <li key={index} className="flex flex-wrap items-center gap-2">
          <span className="text-faint">#{index + 1}</span>
          <select
            value={season.crop}
            aria-label={`Season ${index + 1} crop`}
            onChange={(event) => onPatch(index, { crop: event.target.value })}
            className="rounded border border-line bg-surface px-1 py-0.5 text-fg"
          >
            {PRESET_CROPS.map((crop) => (
              <option key={crop} value={crop}>
                {CROP_REMOVAL[crop].label.split(" ")[0]}
              </option>
            ))}
          </select>
          <label className="flex items-center gap-1">
            <input
              type="number"
              min={0}
              value={goalFor(season)}
              disabled={useHistory && avgs?.[season.crop] != null}
              aria-label={`Season ${index + 1} yield goal`}
              onChange={(event) =>
                onPatch(index, { yieldLevel: Number(event.target.value) || 0 })
              }
              className="tnum w-16 rounded border border-line bg-surface px-1 py-0.5 text-fg disabled:opacity-60"
            />
            bu/ac
          </label>
          <span className="text-faint">
            → {CROP_REMOVAL[season.crop]?.p2o5} P₂O₅ /{" "}
            {CROP_REMOVAL[season.crop]?.k2o} K₂O per bu
          </span>
        </li>
      ))}
    </ul>
  );
}

function HistoryToggle({
  hasHistory,
  useHistory,
  loading,
  onToggle,
}: {
  hasHistory: boolean;
  useHistory: boolean;
  loading: boolean;
  onToggle: (on: boolean) => void;
}) {
  return (
    <label
      className={`flex items-center gap-1.5 ${hasHistory ? "cursor-pointer text-fg" : "text-faint"}`}
    >
      <input
        type="checkbox"
        checked={useHistory}
        disabled={!hasHistory}
        onChange={(event) => onToggle(event.target.checked)}
        aria-label="Use JD yield history for goals"
      />
      Use JD yield history for the goals
      {!hasHistory && " (no yield history on this field)"}
      {loading && <Loader2 size={11} className="animate-spin text-accent" />}
    </label>
  );
}

function SeasonActions({
  busy,
  waiting,
  label,
  onConfirm,
  onCancel,
}: {
  busy: boolean;
  waiting: boolean;
  label: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="flex items-center gap-2">
      {/* Build waits for the averages, so "use yield history" cannot ship the static goals. */}
      <button
        className="rounded bg-accent px-3 py-1.5 text-canvas hover:bg-accent-hover disabled:opacity-50"
        disabled={busy || waiting}
        onClick={onConfirm}
      >
        {busy ? <Loader2 size={12} className="inline animate-spin" /> : null}{" "}
        Build {label}
      </button>
      <button
        className="text-[11px] text-muted hover:text-fg disabled:opacity-50"
        disabled={busy}
        onClick={onCancel}
      >
        Cancel
      </button>
    </div>
  );
}

// The seasons/crops/yield pop-up: how many seasons, each with its crop + yield goal.
export function SeasonsStep({
  field,
  yieldOpts,
  preset,
  busy,
  onConfirm,
  onCancel,
}: {
  field: DashboardField;
  yieldOpts: YieldOpt[];
  preset: FertPreset;
  busy: boolean;
  onConfirm: (seasons: Season[]) => void;
  onCancel: () => void;
}) {
  const [seasons, setSeasons] = useState<Season[]>([defaultSeason("corn")]);
  const [useHistory, setUseHistory] = useState(false);
  const { avgs, loadingAvgs, hasHistory, goalFor } = useCropAverages(
    field.field_id,
    yieldOpts,
    useHistory,
  );
  const confirm = () =>
    onConfirm(
      seasons.map((season) => ({
        crop: season.crop,
        yieldLevel: goalFor(season),
      })),
    );

  return (
    <>
      <SeasonHeading label={preset.label} busy={busy} onCancel={onCancel} />
      <div className="flex flex-col gap-3 overflow-auto p-4 text-[12px]">
        <p className="text-muted">
          Plan the rotation — crop removal is summed across every season (each
          crop with its own removal rate).
        </p>
        <SeasonCount
          count={seasons.length}
          onCount={(count) =>
            setSeasons((current) => resizeSeasons(current, count))
          }
        />
        <SeasonRows
          seasons={seasons}
          goalFor={goalFor}
          useHistory={useHistory}
          avgs={avgs}
          onPatch={(index, patch) =>
            setSeasons((current) => patchSeason(current, index, patch))
          }
        />
        <HistoryToggle
          hasHistory={hasHistory}
          useHistory={useHistory}
          loading={loadingAvgs}
          onToggle={setUseHistory}
        />
        <SeasonActions
          busy={busy}
          waiting={useHistory && avgs === null}
          label={preset.label}
          onConfirm={confirm}
          onCancel={onCancel}
        />
      </div>
    </>
  );
}
